<template>
  <div class="section-birds-eye" :class="[
    readerSlug,
    showSirah ? 'show-sirah' : null,
    showTextReuse ? 'show-text-reuse' : null,
  ]">
    <div class="spyglass" ref="spyglass" :style="{ top: `${spyglassY}px` }" v-if="spyglassContent">{{ spyglassContent }}</div>
    <svg v-if="valid" width="100%" :height="totalLines * lineHeight">
      <rect x="5" width="20" class="section"
        v-for="(superheading, superheadingIndex) in superheadings"
        :height="superheading.sections.length * lineHeight"
        :y="calcSuperheadingStart(superheadingIndex)"
        :key="superheadingIndex"
        :data-content="superheading.label"
        @mouseenter="onMouseEnter"
        @mouseleave="onMouseLeave"
      />
      <template v-for="(superheading, superheadingIndex) in superheadings">
        <template v-for="(section, sectionIndex) in superheading.sections">
          <template v-if="showSirah && showTextReuse">
            <rect
              x="30"
              class="section-line"
              :class="section.highlight ? 'highlight' : 'regular'"
              :key="`${superheadingIndex}-${sectionIndex}-highlight`"
              :height="lineHeight"
              :y="calcSuperheadingStart(superheadingIndex) + (sectionIndex * lineHeight)"
              :data-content="section.textContent"
              @mouseenter="onMouseEnter"
              @mouseleave="onMouseLeave"
              @click="onSelectSirah(section)"
            />
            <rect
              x="30"
              class="section-line"
              :class="section.highlightTextReuse ? 'highlight-text-reuse' : 'regular-reuse'"
              :key="`${superheadingIndex}-${sectionIndex}-text-reuse`"
              :height="lineHeight"
              :y="calcSuperheadingStart(superheadingIndex) + (sectionIndex * lineHeight)"
              :data-content="section.textContent"
              @mouseenter="onMouseEnter"
              @mouseleave="onMouseLeave"
              @click="onSelectTextReuse(section)"
            />
          </template>
          <rect
            v-else
            x="30"
            class="section-line"
            :class="sectionLineClass(section)"
            :key="`${superheadingIndex}-${sectionIndex}`"
            :height="lineHeight"
            :y="calcSuperheadingStart(superheadingIndex) + (sectionIndex * lineHeight)"
            :data-content="section.textContent"
            @mouseenter="onMouseEnter"
            @mouseleave="onMouseLeave"
            @click="onSelect(section)"
          />
        </template>
      </template>
      <polygon v-if="indicatorPoints" :points="indicatorPoints" class="visibility-indicator" />
    </svg>
    <p v-else>{{ $t('invalidOffset') }}</p>
  </div>
</template>

<script>
  import utils from './utils';

  export default {
    props: {
      superheadings: {
        type: Array,
        required: true,
        validator(value) {
          return utils.areValidSuperheadings(value);
        },
      },
      lineHeight: {
        type: Number,
        default: 2,
      },
      readerId: {
        type: String,
      },
      showSirah: {
        type: Boolean,
        default: false,
      },
      showTextReuse: {
        type: Boolean,
        default: false,
      },
    },
    computed: {
      readerSlug() {
        const color = this.$store.getters.readerColor(this.readerId);
        return `reader-${color}`;
      },
      totalLines() {
        return this.superheadings.reduce((prev, superheading) => prev + superheading.sections.length, 0);
      },
      valid() {
        return utils.areValidSuperheadings(this.superheadings);
      },
      visibleSections() {
        const ids = [];
        this.superheadings.forEach(superheading => superheading.sections.forEach((section) => {
          if (section.readerVisible) {
            ids.push(section.label);
          }
        }));
        return ids;
      },
      indicatorPoints() {
        const middleId = this.visibleSections[Math.floor(this.visibleSections.length / 2)];
        let superheadingIndex = null;
        let sectionIndex = null;
        this.superheadings.forEach((superheading, sIndex) => {
          superheading.sections.forEach((section, secIndex) => {
            if (section.label === middleId) {
              superheadingIndex = sIndex;
              sectionIndex = secIndex;
            }
          });
        });
        if (superheadingIndex === null || sectionIndex === null) {
          return null;
        }

        const triangleHeight = this.lineHeight * 6;
        const center = this.calcSuperheadingStart(superheadingIndex) + (sectionIndex * this.lineHeight); // + (triangleHeight / 2)
        const x1 = 0;
        const x2 = triangleHeight;
        const x3 = 0;
        const y1 = center - (triangleHeight / 2);
        const y2 = center;
        const y3 = center + (triangleHeight / 2);
        return `${x1},${y1} ${x2},${y2} ${x3},${y3}`;
      },
    },
    data() {
      return {
        spyglassY: null,
        spyglassContent: null,
      };
    },
    methods: {
      onMouseLeave() {
        this.spyglassY = null;
        this.spyglassContent = null;
      },
      onMouseEnter(a) {
        this.spyglassContent = a.target.dataset.content;
        this.$nextTick(() => {
          const boxHeight = this.$refs.spyglass.getBoundingClientRect().height;
          const componentTop = this.$el.getBoundingClientRect().top;
          this.spyglassY = a.clientY - componentTop - boxHeight - 20;
        });
      },
      sectionLineClass(section) {
        if (section.highlight && section.highlightTextReuse) {
          return 'highlight hightlight-text-reuse';
        }
        if (section.highlight) {
          return 'highlight';
        }
        if (section.highlightTextReuse) {
          return 'highlight-text-reuse';
        }
        return 'regular';
      },
      onSelectSirah(section) {
        let lineIdx = null;
        if (this.showSirah && section.highlight) {
          lineIdx = section.firstSirahLineIdx;
        }
        this.$emit('select', section, lineIdx);
      },
      onSelectTextReuse(section) {
        let lineIdx = null;
        if (this.showTextReuse && section.highlightTextReuse) {
          lineIdx = section.firstAlignmentIdx;
        }
        this.$emit('select', section, lineIdx);
      },
      onSelect(section) {
        let lineIdx = null;
        if (this.showSirah && section.highlight) {
          lineIdx = section.firstSirahLineIdx;
        } else if (this.showTextReuse && section.highlightTextReuse) {
          lineIdx = section.firstAlignmentIdx;
        }
        this.$emit('select', section, lineIdx);
      },
      calcSuperheadingStart(superheadingIndex) {
        return this.superheadings.reduce((prev, superheading, index) => {
          if (index < superheadingIndex) {
            return prev + superheading.sections.length * this.lineHeight;
          }
          return prev;
        }, 0);
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import '../variables';
  #app[data-locale="ar"] {
    .spyglass {
      font-family: $ar-font-family;
      font-size: $ar-text-md;
      width: 150%;
      direction: rtl;
      margin-left: unset;
      margin-right: 40%;
    }
  }

  rect.section:nth-child(odd) {
    fill: $gray-200;
  }
  rect.section:nth-child(even) {
    fill: $gray-400;
  }
  rect.section-line {
    width: calc(100% - 30px);
    cursor: pointer;
    &:hover {
        fill: $brand;
    }
  }
  .section-birds-eye {
    position: relative;
  }
  .spyglass {
    position: absolute;
    width: 180%;
    margin-left: -40%;
    background: $white;
    padding: 10px 15px;
    border-radius: 5px;
    border: 1px solid $gray-100;
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.16);
  }
  rect.highlight {
    fill: $gray-400;
  }
  rect.highlight-text-reuse {
    fill: $text-reuse-background;
  }
  rect.regular,
  rect.regular-reuse {
    fill: transparent;
  }

  .show-sirah {

  }
  .show-text-reuse {

  }
  .show-sirah.show-text-reuse {
    rect.section-line {
      width: calc(50% - 15px);
      &.highlight-text-reuse,
      &.regular-reuse {
        transform: translateX(calc(50% - 15px));
      }
    }
  }

  .visibility-indicator {
    fill: $brand;
  }

  .reader-1 {
    rect.highlight:hover,
    rect.regular:hover,
    .visibility-indicator {
      fill: $reader-1;
    }
  }
  .reader-2 {
    rect.highlight:hover,
    rect.regular:hover,
    .visibility-indicator {
      fill: $reader-2;
    }
  }
  .reader-3 {
    rect.highlight:hover,
    rect.regular:hover,
    .visibility-indicator {
      fill: $reader-3;
    }
  }
</style>
